import { X } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { useT } from "@/i18n/provider";
import { cx } from "./cx";
import { usePhoneWidth } from "./use-media";

/**
 * A box over the dimmed page (the phone's Popup and Sheet).
 *
 *   - Focus moves inside when it opens, stays inside while it is open (Tab
 *     goes round), and goes back to what was focused before when it closes.
 *   - Escape, the × and a click on the dimmed page close it, unless
 *     `dismissible={false}`: for what is shown only once (a new password),
 *     which must be closed on purpose, as on the phone.
 *   - The page behind does not scroll while it is open.
 *
 * Buttons go in `footer`: at the bottom right on a computer, stacked across
 * the width on a phone.
 */

export interface DialogProps {
  title: string;
  onClose: () => void;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** 480 px for a question, 640 px for a form. */
  size?: "confirm" | "form";
  dismissible?: boolean;
}

export function Dialog(props: DialogProps) {
  return <Overlay {...props} placement="centre" />;
}

/**
 * For a short question on top of a page ("Why?", "Change price"): a panel from
 * the bottom on phone width, where a thumb can reach it; a Dialog on wider screens.
 */
export function Sheet(props: DialogProps) {
  const phone = usePhoneWidth();
  return <Overlay {...props} placement={phone ? "bottom" : "centre"} />;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function Overlay({
  title,
  onClose,
  children,
  footer,
  size = "confirm",
  dismissible = true,
  placement,
}: DialogProps & { placement: "centre" | "bottom" }) {
  const t = useT();
  const titleId = useId();
  const box = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  useLayoutEffect(() => {
    closeRef.current = onClose;
  });

  // Focus in on open, back out on close; the page behind stays still.
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    const chosen =
      box.current?.querySelector<HTMLElement>("[data-autofocus]") ??
      box.current?.querySelector<HTMLElement>("input, select, textarea");
    (chosen ?? box.current)?.focus();
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
      before?.focus?.();
    };
  }, []);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.stopPropagation();
      if (dismissible) closeRef.current();
      return;
    }
    if (e.key !== "Tab" || !box.current) return;
    const items = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    if (e.shiftKey && (document.activeElement === first || document.activeElement === box.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  const bottom = placement === "bottom";

  return createPortal(
    <div
      className={cx("fixed inset-0 z-50 flex bg-overlay", bottom ? "items-end" : "items-center justify-center p-4")}
      onMouseDown={(e) => {
        // Only a press that starts on the dimmed page itself, not a drag out of a field.
        if (e.target === e.currentTarget && dismissible) closeRef.current();
      }}
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className={cx(
          "flex max-h-[85vh] w-full flex-col bg-surface shadow-card outline-none dark:border dark:border-border",
          bottom ? "rounded-t-card" : "rounded-card",
          !bottom && (size === "form" ? "max-w-[640px]" : "max-w-[480px]"),
        )}
      >
        <div className="flex min-h-14 items-center gap-3 pt-2 pr-2 pl-4">
          <h2 id={titleId} className="flex-1 text-heading font-bold text-text">
            {title}
          </h2>
          {dismissible && (
            <button
              type="button"
              onClick={() => closeRef.current()}
              aria-label={t.close}
              className="flex size-10 items-center justify-center rounded-full text-body hover:bg-primary-soft hover:text-primary"
            >
              <X size={22} aria-hidden="true" />
            </button>
          )}
        </div>
        {children && <div className="flex flex-col gap-4 overflow-y-auto px-4 pt-1 pb-4">{children}</div>}
        {footer && (
          <div className="flex flex-col-reverse gap-2 border-t border-border px-4 py-3 md:flex-row md:justify-end">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
